class ReviewsStarRating extends HTMLElement {
  constructor() {
    super();

    this.jsOkendoStarRating = null;
    this.reviewCountContainer = null;
    this.averateRatingContainer = null;
    this.observerMutation = null;
    this.starIconContainer = null;
    this.reviewCount = 0;
    this.averageRating = 0;
    this.formattedAverageRating = 0;
    this.observerIntersection = null;
    this.reInitTimer = 3000;
    this.callMutationTimer = 2000;
    this.starReviewLinks = null;
    this.aggregateRatingInjected = false;
  }

  connectedCallback() {
    this.reviewCountContainer = this.querySelectorAll('[data-review-count]');
    this.averateRatingContainer = this.querySelectorAll(
      '[data-averate-rating]'
    );
    this.starIconContainer = this.querySelectorAll('[data-star-icon]');
    this.jsOkendoStarRating = this.querySelector('[data-custom-star-rating]');
    this.starReviewLinks = this.querySelectorAll('[data-star-review-link]');

    this.observerIntersection = new IntersectionObserver(
      this.observationCallback.bind(this),
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    this.observerIntersection.observe(this);
  }

  observationCallback(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.initOkendoReviews(true);
        observer.unobserve(entry.target);
      }
    });
  }

  initOkendoReviews(initialCall) {
    const isOkendoWidgetInit = this.getAttribute('data-review-init');

    if (isOkendoWidgetInit === 'true') {
      console.info('Okendo Already initialzed');

      return null;
    }

    if (!this.jsOkendoStarRating) {
      console.warn('Missing Okendo Star Rating Element');

      return null;
    }

    if (
      !window.okeWidgetApi &&
      typeof window.okeWidgetApi?.initWidget !== 'function'
    ) {
      console.warn('Okendo Widget API Missing');

      setTimeout(() => {
        this.initOkendoWidget();
      }, this.reInitTimer);

      return null;
    }

    if (
      window.okeWidgetApi &&
      typeof window.okeWidgetApi.initWidget === 'function'
    ) {
      if (initialCall) {
        this.initOkendoWidget();
      }
    }
  }

  initOkendoWidget() {
    this.jsOkendoStarRating?.setAttribute('data-oke-star-rating', '');
    this.setAttribute('data-review-init', 'true');
    window.okeWidgetApi?.initWidget(this.jsOkendoStarRating, true);

    this.observeOkendoRatings();
  }

  observeOkendoRatings() {
    // Create the MutationObserver
    this.observerMutation = new MutationObserver(
      this.observerMutationCallback.bind(this)
    );

    // Configure the observer to watch for child list changes
    const config = {
      childList: true,
      attributes: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true,
    };

    // Start observing the list container for mutations
    this.observerMutation.observe(this.jsOkendoStarRating, config);
  }

  observerMutationCallback(mutationsList, _) {
    for (const mutation of mutationsList) {
      // Check if the mutation is a childList change and new nodes were added
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (!node.tagName) {
            return null;
          }

          if (node.hasAttribute('data-oke-container')) {
            setTimeout(() => {
              const reviewJson = this.getReviewJsonFromNode(node);

              this.reviewCount = reviewJson?.reviewCount || 0;
              this.averageRating = reviewJson?.averageRating || "0";
              this.formattedAverageRating = reviewJson?.formattedAverageRating || "0";

              this.setRatingsToContainers();

              // Inject aggregate rating into schema only once to prevent duplicate updates
              if (this.aggregateRatingInjected === false) {
                this.injectAggregateReviewsIntoSchema(reviewJson);
                this.aggregateRatingInjected = true;
              }
            }, this.callMutationTimer);
          }
        });
      }
    }
  }

  getReviewJsonFromNode(node) {
    if (!node || !node.lastChild) return null;

    try {
      const reviewJson = JSON.parse(node.lastChild.textContent);
      const formattedAverageRating = reviewJson.averageRating
        ? reviewJson.averageRating.toString().split('.')[0]
        : "0";

      const reviewCount = reviewJson.reviewCount || 0;

      return {
        averageRating: reviewJson.averageRating || 0,
        formattedAverageRating,
        reviewCount
      };
    } catch (e) {
      console.error('Failed to parse review JSON:', e);
      return null;
    }
  }

  setRatingsToContainers() {
    this.reviewCountContainer?.forEach((container) => {
      container.innerHTML = this.reviewCount;
    });

    this.averateRatingContainer?.forEach((container) => {
      container.innerHTML = this.formattedAverageRating;
    });

    this.starIconContainer?.forEach((container) => {
      if (this.formattedAverageRating > 0) {
        container.classList.remove('zero-rating');
      }
    });

    this.starReviewLinks?.forEach((container) => {
      container.classList.remove('hidden');
    });
  }

  /**
   * Updates the Product or ProductGroup schema with aggregate review data.
   * Locates the relevant JSON-LD script and injects the AggregateRating object.
   * @param {Object} reviewJson - The review data containing averageRating and reviewCount.
   */
  injectAggregateReviewsIntoSchema(reviewJson) {
    if (!reviewJson.averageRating || !reviewJson.reviewCount) return;

    const schemaScript = document.querySelector(
      'script[type="application/ld+json"][data-schema="product"]'
    );

    if (!schemaScript) return;

    try {
      // Clean and parse the schema JSON
      const cleanSchemaScriptString = schemaScript.textContent
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
        .trim();
      let schema = JSON.parse(cleanSchemaScriptString);

      // Target Product or ProductGroup directly
      const target =
        schema["@type"] === "Product" || schema["@type"] === "ProductGroup"
          ? schema
          : null;

      if (target) {
        target.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: reviewJson.averageRating.toString(),
          reviewCount: reviewJson.reviewCount.toString(),
        };

        schemaScript.textContent = JSON.stringify(schema);
      }
    } catch (e) {
      console.error("Error updating Product/ProductGroup schema:", e);
    }
  }

  disconnectedCallback() {
    if (this.observerMutation) {
      this.observerMutation.disconnect();
    }

    if (this.observerIntersection) {
      this.observerIntersection.disconnect();
    }
  }
}

customElements.define('reviews-star-rating-controller', ReviewsStarRating);
